import {getOptImage} from 'epic-ue-shared';
import {
    BackgroundYouTubeVideoProps,
    BackgroundTencentVideoProps,
    BackgroundEmbedVideoProps,
    LocalVideoOptionsArray,
    BackgroundVideoOptions,
    BackgroundVideoOptionsArray,
    ReactCommonBackgroundVideoProps,
    ReactCommonBackgroundVideoOptions,
    BackgroundHtmlVideoProps,
    FallbackImageProps,
    HTML_VIDEO_TYPE,
    FALLBACK_IMAGE_TYPE,
    EMBED_VIDEO_TYPE,
    TENCENT_VIDEO_TYPE,
    YOUTUBE_VIDEO_TYPE
} from '../types';

/**
 * Convert our specific video fields into the generic common background object.
 * @param mediaOptions
 */
export const convertToCommonBackgroundVideo = (
    mediaOptions: BackgroundVideoOptions | BackgroundVideoOptionsArray
): ReactCommonBackgroundVideoProps => {
    const mediaOptionArray = mediaOptions as BackgroundVideoOptionsArray;
    let media: BackgroundVideoOptions = {};
    let type: ReactCommonBackgroundVideoOptions = 'image';
    let typeDetermined = false;

    //check if cms functional array and convert
    if (mediaOptionArray && mediaOptionArray.length && mediaOptionArray[0]) {
        const item = mediaOptionArray[0];
        if (item.htmlVideo) {
            media = item.htmlVideo as BackgroundHtmlVideoProps;
            type = 'htmlVideo';
            typeDetermined = true;
        } else if (item.youtubeVideo) {
            media = item.youtubeVideo as BackgroundYouTubeVideoProps;
            type = 'youtubeVideo';
            typeDetermined = true;
        } else if (item.tencentVideo) {
            media = item.tencentVideo as BackgroundTencentVideoProps;
            type = 'tencentVideo';
            typeDetermined = true;
        } else if (item.embedVideo) {
            media = item.embedVideo as BackgroundEmbedVideoProps;
            type = 'embedVideo';
            typeDetermined = true;
        } else if (item.image) {
            media = item.image as FallbackImageProps;
            type = 'image';
            typeDetermined = true;
        }
    } else {
        media = mediaOptions as BackgroundVideoOptions;
    }

    const allOptions: BackgroundVideoOptions = media || {};
    const {_type} = allOptions;
    const {src = ''} = allOptions as FallbackImageProps;
    const {content = [], image} = allOptions as BackgroundHtmlVideoProps;

    const background: ReactCommonBackgroundVideoProps = {
        type,
        ...allOptions,
        content
    };

    //check the cms meta field _type to determine the type
    if (!typeDetermined) {
        switch (_type) {
            case HTML_VIDEO_TYPE:
                background.type = 'htmlVideo';
                typeDetermined = true;
                break;
            case YOUTUBE_VIDEO_TYPE:
                background.type = 'youtubeVideo';
                typeDetermined = true;
                break;
            case TENCENT_VIDEO_TYPE:
                background.type = 'tencentVideo';
                typeDetermined = true;
                break;
            case EMBED_VIDEO_TYPE:
                background.type = 'embedVideo';
                typeDetermined = true;
                break;
            case FALLBACK_IMAGE_TYPE:
                background.type = 'image';
                typeDetermined = true;
                background.content.push(getOptImage({url: src}));
                break;
            default:
                break;
        }
    }

    const firstContent = content && content.length ? content[0] : '';
    const firstContentIsImage = /\.(jpg|png|gif|jpeg)$/.test(firstContent);

    //look up the file extension to determine type
    if (!typeDetermined && content && content.length) {
        if (/\.(mp4|webm|avi)$/.test(firstContent)) {
            background.type = 'htmlVideo';
        } else if (firstContentIsImage) {
            background.type = 'image';
        }
    }

    //set images
    const fallbackImageSrc = src || image || (firstContentIsImage && firstContent) || '';
    background.mobileImage = getOptImage({url: fallbackImageSrc, width: 800});
    background.tabletImage = getOptImage({url: fallbackImageSrc, width: 1200});
    background.image = getOptImage({url: fallbackImageSrc});

    //validate the expected content is there for each type otherwise fallback to image
    if (fallbackImageSrc) {
        if (background.type === 'embedVideo' && !background.embedSrc) {
            background.type = 'image';
        } else if (background.type === 'youtubeVideo' && !background.contentId) {
            background.type = 'image';
        } else if (background.type === 'htmlVideo' && !background.content.length) {
            background.type = 'image';
        }
    }

    if (background.content && !background.content.length) {
        background.content.push(background.image);
    }

    return background;
};

/**
 * Returns true if any object inside a mediaOptions array has any key besides _type.
 * @param mediaOptions
 */
export const isValidMediaOptions = (
    mediaOptions: BackgroundVideoOptionsArray | LocalVideoOptionsArray
): boolean => {
    if (mediaOptions && mediaOptions.length) {
        for (let i = 0; i < mediaOptions.length; i++) {
            if (Object.keys(mediaOptions[i]).filter((k) => k !== '_type').length) {
                return true;
            }
        }
    }
    return false;
};

export const htmlVideoInMediaOptions = (
    mediaOptions: BackgroundVideoOptionsArray | LocalVideoOptionsArray
): BackgroundVideoOptionsArray | LocalVideoOptionsArray => {
    const result: BackgroundVideoOptionsArray | LocalVideoOptionsArray = [];
    if (mediaOptions && mediaOptions.length) {
        for (let i = 0; i < mediaOptions.length; i++) {
            if (Object.keys(mediaOptions[i]).indexOf('htmlVideo') >= 0) {
                result.push(mediaOptions[i]);
            }
        }
    }
    return result;
};

export const replaceYoutubeCookieEmbeds = (background?: ReactCommonBackgroundVideoProps) => {
    if (background && background.embedSrc) {
        background.embedSrc = background.embedSrc.replace(/youtube\.com/ig, 'youtube-nocookie.com');
    }
}
